import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export const supportedLocales = ["en", "tr", "es", "pt", "de", "fr", "ru", "ar", "hi", "id"] as const;

export type AppLocale = (typeof supportedLocales)[number];

const LOCALE_STORAGE_KEY = "varnax-locale";

export const localeOptions: ReadonlyArray<{ value: AppLocale; shortLabel: string; label: string }> = [
  { value: "en", shortLabel: "EN", label: "English" },
  { value: "tr", shortLabel: "TR", label: "Türkçe" },
  { value: "es", shortLabel: "ES", label: "Español" },
  { value: "pt", shortLabel: "PT", label: "Português" },
  { value: "de", shortLabel: "DE", label: "Deutsch" },
  { value: "fr", shortLabel: "FR", label: "Français" },
  { value: "ru", shortLabel: "RU", label: "Русский" },
  { value: "ar", shortLabel: "AR", label: "العربية" },
  { value: "hi", shortLabel: "HI", label: "हिन्दी" },
  { value: "id", shortLabel: "ID", label: "Bahasa Indonesia" },
];

const supportedLocaleSet = new Set<string>(supportedLocales);

const normalizeLocale = (value?: string | null): AppLocale | null => {
  const language = value?.trim().toLowerCase().replace(/_/g, "-").split("-")[0] ?? "";
  return supportedLocaleSet.has(language) ? language as AppLocale : null;
};

export const resolveLocale = (
  storedLocale?: string | null,
  browserLocales?: string | readonly string[] | null,
): AppLocale => {
  const stored = normalizeLocale(storedLocale);
  if (stored) return stored;
  const candidates = typeof browserLocales === "string" ? [browserLocales] : browserLocales ?? [];
  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate);
    if (locale) return locale;
  }
  return "en";
};

export const intlLocale: Record<AppLocale, string> = {
  en: "en-US", tr: "tr-TR", es: "es-ES", pt: "pt-BR", de: "de-DE",
  fr: "fr-FR", ru: "ru-RU", ar: "ar", hi: "hi-IN", id: "id-ID",
};

export const localeDirection = (locale: AppLocale): "ltr" | "rtl" => locale === "ar" ? "rtl" : "ltr";

interface LocaleContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => resolveLocale(
    localStorage.getItem(LOCALE_STORAGE_KEY),
    navigator.languages.length > 0 ? navigator.languages : [navigator.language],
  ));

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDirection(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = useCallback((nextLocale: AppLocale) => setLocaleState(nextLocale), []);
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used inside LocaleProvider");
  return context;
}

interface MenuCopy {
  activeArenas: string;
  arenaNameError: string;
  arenaOfflineError: string;
  arenaOnline: string;
  battle: string;
  chooseArena: string;
  connecting: string;
  endlessArena: string;
  evolution: string;
  instantMatch: string;
  language: string;
  level: string;
  navigation: Record<"evolutions" | "armory" | "missions" | "shop" | "ranks", string>;
  entering: string;
  matchmaking: string;
  online: string;
  openArena: string;
  party: string;
  partyCode: string;
  partyCopied: string;
  partyCopy: string;
  partyCreate: string;
  partySave: string;
  partySaved: string;
  partyHint: string;
  partyInvalid: string;
  partySolo: string;
  play: string;
  seasonLive: string;
  server: string;
  serverPending: string;
  tagline: string;
  warriorName: string;
  yourWarrior: string;
}

export const menuCopy: Record<AppLocale, MenuCopy> = {
  en: {
    activeArenas: "ACTIVE ARENAS",
    arenaNameError: "Arena name must contain 3–20 characters.",
    arenaOfflineError: "The arena is temporarily offline. Try again shortly.",
    arenaOnline: "ARENA ONLINE",
    battle: "BATTLE",
    chooseArena: "Enter the arena",
    connecting: "CONNECTING",
    endlessArena: "ARENA",
    evolution: "YOUR EVOLUTION",
    instantMatch: "INSTANT MATCH",
    language: "Language",
    level: "LEVEL",
    navigation: { evolutions: "Evolve", armory: "Armory", missions: "Missions", shop: "Shop", ranks: "Ranks" },
    entering: "ENTERING…",
    matchmaking: "MATCHMAKING",
    online: "ONLINE",
    openArena: "OPEN ARENA",
    party: "PARTY",
    partyCode: "INVITE CODE",
    partyCopied: "COPIED",
    partyCopy: "COPY LINK",
    partyCreate: "NEW CODE",
    partySave: "SAVE & JOIN",
    partySaved: "PARTY READY",
    partyHint: "Party members share one arena and appear on the minimap.",
    partyInvalid: "Paste a VARNAX invite link or enter the complete 12-character code, then save it.",
    partySolo: "SOLO",
    play: "PLAY",
    seasonLive: "ARENA · LIVE",
    server: "SERVER",
    serverPending: "CHECKING SERVER",
    tagline: "OUTGROW THE ARENA",
    warriorName: "WARRIOR NAME",
    yourWarrior: "YOUR WARRIOR",
  },
  tr: {
    activeArenas: "AKTİF ARENA",
    arenaNameError: "Savaşçı adı 3–20 karakter içermelidir.",
    arenaOfflineError: "Arena geçici olarak kapalı. Kısa süre sonra tekrar dene.",
    arenaOnline: "ARENA AÇIK",
    battle: "SAVAŞ",
    chooseArena: "Arenaya gir",
    connecting: "BAĞLANIYOR",
    endlessArena: "ARENA",
    evolution: "EVRİMİN",
    instantMatch: "ANINDA MAÇ",
    language: "Dil",
    level: "SEVİYE",
    navigation: { evolutions: "Evrimler", armory: "Cephanelik", missions: "Görevler", shop: "Mağaza", ranks: "Sıralama" },
    entering: "GİRİLİYOR…",
    matchmaking: "RAKİP ARANIYOR",
    online: "ÇEVRİMİÇİ",
    openArena: "AÇIK ARENA",
    party: "EKİP",
    partyCode: "DAVET KODU",
    partyCopied: "KOPYALANDI",
    partyCopy: "LİNKİ KOPYALA",
    partyCreate: "YENİ KOD",
    partySave: "KAYDET",
    partySaved: "EKİP HAZIR",
    partyHint: "Ekip üyeleri aynı arenaya girer ve mini haritada birbirini görür.",
    partyInvalid: "VARNAX davet bağlantısını yapıştır veya 12 karakterlik kodu girip kaydet.",
    partySolo: "TEK",
    play: "OYNA",
    seasonLive: "ARENA · CANLI",
    server: "SUNUCU",
    serverPending: "SUNUCU KONTROL EDİLİYOR",
    tagline: "ARENAYI AŞ",
    warriorName: "SAVAŞÇI ADI",
    yourWarrior: "SAVAŞÇIN",
  },
  es: {
    activeArenas: "ARENAS ACTIVAS", arenaNameError: "El nombre debe tener entre 3 y 20 caracteres.", arenaOfflineError: "La arena no está disponible temporalmente. Inténtalo de nuevo pronto.", arenaOnline: "ARENA EN LÍNEA", battle: "BATALLA", chooseArena: "Entra en la arena", connecting: "CONECTANDO", endlessArena: "ARENA", evolution: "TU EVOLUCIÓN", instantMatch: "PARTIDA INSTANTÁNEA", language: "Idioma", level: "NIVEL", navigation: { evolutions: "Evoluciones", armory: "Arsenal", missions: "Misiones", shop: "Tienda", ranks: "Clasificación" }, entering: "ENTRANDO…", matchmaking: "BUSCANDO RIVALES", online: "EN LÍNEA", openArena: "ARENA ABIERTA", party: "EQUIPO", partyCode: "CÓDIGO DE INVITACIÓN", partyCopied: "COPIADO", partyCopy: "COPIAR ENLACE", partyCreate: "NUEVO CÓDIGO", partySave: "GUARDAR Y UNIRSE", partySaved: "EQUIPO LISTO", partyHint: "Los miembros entran en la misma arena y aparecen en el minimapa.", partyInvalid: "Pega un enlace de VARNAX o escribe el código completo de 12 caracteres y guárdalo.", partySolo: "SOLO", play: "JUGAR", seasonLive: "ARENA · EN VIVO", server: "SERVIDOR", serverPending: "COMPROBANDO SERVIDOR", tagline: "SUPERA LA ARENA", warriorName: "NOMBRE DEL GUERRERO", yourWarrior: "TU GUERRERO",
  },
  pt: {
    activeArenas: "ARENAS ATIVAS", arenaNameError: "O nome deve ter entre 3 e 20 caracteres.", arenaOfflineError: "A arena está temporariamente indisponível. Tente novamente em breve.", arenaOnline: "ARENA ONLINE", battle: "BATALHA", chooseArena: "Entre na arena", connecting: "CONECTANDO", endlessArena: "ARENA", evolution: "SUA EVOLUÇÃO", instantMatch: "PARTIDA INSTANTÂNEA", language: "Idioma", level: "NÍVEL", navigation: { evolutions: "Evoluções", armory: "Arsenal", missions: "Missões", shop: "Loja", ranks: "Ranking" }, entering: "ENTRANDO…", matchmaking: "BUSCANDO RIVAIS", online: "ONLINE", openArena: "ARENA ABERTA", party: "GRUPO", partyCode: "CÓDIGO DE CONVITE", partyCopied: "COPIADO", partyCopy: "COPIAR LINK", partyCreate: "NOVO CÓDIGO", partySave: "SALVAR E ENTRAR", partySaved: "GRUPO PRONTO", partyHint: "O grupo entra na mesma arena e aparece no minimapa.", partyInvalid: "Cole um link VARNAX ou digite o código completo de 12 caracteres e salve.", partySolo: "SOLO", play: "JOGAR", seasonLive: "ARENA · AO VIVO", server: "SERVIDOR", serverPending: "VERIFICANDO SERVIDOR", tagline: "SUPERE A ARENA", warriorName: "NOME DO GUERREIRO", yourWarrior: "SEU GUERREIRO",
  },
  de: {
    activeArenas: "AKTIVE ARENEN", arenaNameError: "Der Name muss 3–20 Zeichen lang sein.", arenaOfflineError: "Die Arena ist vorübergehend nicht erreichbar. Versuche es gleich erneut.", arenaOnline: "ARENA ONLINE", battle: "KAMPF", chooseArena: "Betritt die Arena", connecting: "VERBINDEN", endlessArena: "ARENA", evolution: "DEINE EVOLUTION", instantMatch: "SOFORTMATCH", language: "Sprache", level: "STUFE", navigation: { evolutions: "Evolutionen", armory: "Arsenal", missions: "Missionen", shop: "Shop", ranks: "Rangliste" }, entering: "BEITRETEN…", matchmaking: "GEGNER WERDEN GESUCHT", online: "ONLINE", openArena: "OFFENE ARENA", party: "GRUPPE", partyCode: "EINLADUNGSCODE", partyCopied: "KOPIERT", partyCopy: "LINK KOPIEREN", partyCreate: "NEUER CODE", partySave: "SPEICHERN & BEITRETEN", partySaved: "GRUPPE BEREIT", partyHint: "Gruppenmitglieder landen in derselben Arena und erscheinen auf der Minikarte.", partyInvalid: "Füge einen VARNAX-Link oder den vollständigen 12-stelligen Code ein und speichere ihn.", partySolo: "SOLO", play: "SPIELEN", seasonLive: "ARENA · LIVE", server: "SERVER", serverPending: "SERVER WIRD GEPRÜFT", tagline: "ÜBERWACHSE DIE ARENA", warriorName: "KRIEGERNAME", yourWarrior: "DEIN KRIEGER",
  },
  fr: {
    activeArenas: "ARÈNES ACTIVES", arenaNameError: "Le nom doit comporter entre 3 et 20 caractères.", arenaOfflineError: "L’arène est temporairement indisponible. Réessaie bientôt.", arenaOnline: "ARÈNE EN LIGNE", battle: "COMBAT", chooseArena: "Entre dans l’arène", connecting: "CONNEXION", endlessArena: "ARÈNE", evolution: "TON ÉVOLUTION", instantMatch: "PARTIE INSTANTANÉE", language: "Langue", level: "NIVEAU", navigation: { evolutions: "Évolutions", armory: "Arsenal", missions: "Missions", shop: "Boutique", ranks: "Classement" }, entering: "ENTRÉE…", matchmaking: "RECHERCHE D’ADVERSAIRES", online: "EN LIGNE", openArena: "ARÈNE OUVERTE", party: "ÉQUIPE", partyCode: "CODE D’INVITATION", partyCopied: "COPIÉ", partyCopy: "COPIER LE LIEN", partyCreate: "NOUVEAU CODE", partySave: "ENREGISTRER ET REJOINDRE", partySaved: "ÉQUIPE PRÊTE", partyHint: "Les membres rejoignent la même arène et apparaissent sur la minicarte.", partyInvalid: "Colle un lien VARNAX ou saisis le code complet de 12 caractères, puis enregistre-le.", partySolo: "SOLO", play: "JOUER", seasonLive: "ARÈNE · EN DIRECT", server: "SERVEUR", serverPending: "VÉRIFICATION DU SERVEUR", tagline: "DÉPASSE L’ARÈNE", warriorName: "NOM DU GUERRIER", yourWarrior: "TON GUERRIER",
  },
  ru: {
    activeArenas: "АКТИВНЫЕ АРЕНЫ", arenaNameError: "Имя должно содержать от 3 до 20 символов.", arenaOfflineError: "Арена временно недоступна. Повторите попытку позже.", arenaOnline: "АРЕНА В СЕТИ", battle: "БИТВА", chooseArena: "Войти на арену", connecting: "ПОДКЛЮЧЕНИЕ", endlessArena: "АРЕНА", evolution: "ВАША ЭВОЛЮЦИЯ", instantMatch: "БЫСТРЫЙ БОЙ", language: "Язык", level: "УРОВЕНЬ", navigation: { evolutions: "Эволюции", armory: "Арсенал", missions: "Задания", shop: "Магазин", ranks: "Рейтинг" }, entering: "ВХОД…", matchmaking: "ПОИСК СОПЕРНИКОВ", online: "В СЕТИ", openArena: "ОТКРЫТАЯ АРЕНА", party: "ГРУППА", partyCode: "КОД ПРИГЛАШЕНИЯ", partyCopied: "СКОПИРОВАНО", partyCopy: "КОПИРОВАТЬ ССЫЛКУ", partyCreate: "НОВЫЙ КОД", partySave: "СОХРАНИТЬ И ВОЙТИ", partySaved: "ГРУППА ГОТОВА", partyHint: "Участники попадают на одну арену и видят друг друга на мини-карте.", partyInvalid: "Вставьте ссылку VARNAX или полный 12-значный код и сохраните его.", partySolo: "СОЛО", play: "ИГРАТЬ", seasonLive: "АРЕНА · В ЭФИРЕ", server: "СЕРВЕР", serverPending: "ПРОВЕРКА СЕРВЕРА", tagline: "ПРЕВЗОЙДИ АРЕНУ", warriorName: "ИМЯ ВОИНА", yourWarrior: "ВАШ ВОИН",
  },
  ar: {
    activeArenas: "الساحات النشطة", arenaNameError: "يجب أن يتكون الاسم من 3 إلى 20 حرفًا.", arenaOfflineError: "الساحة غير متاحة مؤقتًا. حاول مجددًا بعد قليل.", arenaOnline: "الساحة متصلة", battle: "معركة", chooseArena: "ادخل الساحة", connecting: "جارٍ الاتصال", endlessArena: "الساحة", evolution: "تطورك", instantMatch: "مباراة فورية", language: "اللغة", level: "المستوى", navigation: { evolutions: "التطورات", armory: "الترسانة", missions: "المهام", shop: "المتجر", ranks: "التصنيف" }, entering: "جارٍ الدخول…", matchmaking: "البحث عن منافسين", online: "متصل", openArena: "ساحة مفتوحة", party: "الفريق", partyCode: "رمز الدعوة", partyCopied: "تم النسخ", partyCopy: "نسخ الرابط", partyCreate: "رمز جديد", partySave: "حفظ وانضمام", partySaved: "الفريق جاهز", partyHint: "يدخل أعضاء الفريق الساحة نفسها ويظهرون على الخريطة المصغرة.", partyInvalid: "ألصق رابط VARNAX أو أدخل الرمز الكامل المكون من 12 حرفًا ثم احفظه.", partySolo: "فردي", play: "العب", seasonLive: "الساحة · مباشر", server: "الخادم", serverPending: "جارٍ فحص الخادم", tagline: "تفوّق على الساحة", warriorName: "اسم المحارب", yourWarrior: "محاربك",
  },
  hi: {
    activeArenas: "सक्रिय एरीना", arenaNameError: "नाम 3–20 अक्षरों का होना चाहिए।", arenaOfflineError: "एरीना अभी उपलब्ध नहीं है। थोड़ी देर में फिर प्रयास करें।", arenaOnline: "एरीना ऑनलाइन", battle: "युद्ध", chooseArena: "एरीना में प्रवेश करें", connecting: "कनेक्ट हो रहा है", endlessArena: "एरीना", evolution: "आपका विकास", instantMatch: "तुरंत मैच", language: "भाषा", level: "स्तर", navigation: { evolutions: "विकास", armory: "शस्त्रागार", missions: "मिशन", shop: "दुकान", ranks: "रैंकिंग" }, entering: "प्रवेश हो रहा है…", matchmaking: "प्रतिद्वंद्वी खोजे जा रहे हैं", online: "ऑनलाइन", openArena: "खुला एरीना", party: "दल", partyCode: "आमंत्रण कोड", partyCopied: "कॉपी हुआ", partyCopy: "लिंक कॉपी करें", partyCreate: "नया कोड", partySave: "सहेजें और जुड़ें", partySaved: "दल तैयार", partyHint: "दल के सदस्य एक ही एरीना में जाते हैं और मिनीमैप पर दिखते हैं।", partyInvalid: "VARNAX आमंत्रण लिंक चिपकाएँ या पूरा 12-अक्षर कोड डालकर सहेजें।", partySolo: "एकल", play: "खेलें", seasonLive: "एरीना · लाइव", server: "सर्वर", serverPending: "सर्वर जाँचा जा रहा है", tagline: "एरीना से आगे बढ़ें", warriorName: "योद्धा का नाम", yourWarrior: "आपका योद्धा",
  },
  id: {
    activeArenas: "ARENA AKTIF", arenaNameError: "Nama harus terdiri dari 3–20 karakter.", arenaOfflineError: "Arena sementara tidak tersedia. Coba lagi sebentar lagi.", arenaOnline: "ARENA ONLINE", battle: "BERTARUNG", chooseArena: "Masuki arena", connecting: "MENGHUBUNGKAN", endlessArena: "ARENA", evolution: "EVOLUSIMU", instantMatch: "LAGA INSTAN", language: "Bahasa", level: "LEVEL", navigation: { evolutions: "Evolusi", armory: "Arsenal", missions: "Misi", shop: "Toko", ranks: "Peringkat" }, entering: "MEMASUKI…", matchmaking: "MENCARI LAWAN", online: "ONLINE", openArena: "ARENA TERBUKA", party: "REGU", partyCode: "KODE UNDANGAN", partyCopied: "TERSALIN", partyCopy: "SALIN TAUTAN", partyCreate: "KODE BARU", partySave: "SIMPAN & GABUNG", partySaved: "REGU SIAP", partyHint: "Anggota regu masuk ke arena yang sama dan terlihat di minimap.", partyInvalid: "Tempel tautan VARNAX atau masukkan kode 12 karakter lengkap, lalu simpan.", partySolo: "SOLO", play: "MAIN", seasonLive: "ARENA · LANGSUNG", server: "SERVER", serverPending: "MEMERIKSA SERVER", tagline: "LAMPAUI ARENA", warriorName: "NAMA PEJUANG", yourWarrior: "PEJUANGMU",
  },
};
